// Times as the editor shows them. The timeline keeps whole milliseconds; fields take seconds.

/**
 * "0:04.2": minutes, seconds and tenths, for the clock and the blocks. A time is rounded to the
 * nearest tenth, so a clip split in two reads as the two halves of its length (2.49 s and 1.51 s
 * are 0:02.5 and 0:01.5, not 0:02.4 and 0:01.5), and a block's label and its end agree.
 */
export function formatClock(ms: number): string {
  const tenths = Math.max(0, Math.round(ms / 100));
  const minutes = Math.floor(tenths / 600);
  const seconds = Math.floor((tenths % 600) / 10);
  return `${minutes}:${String(seconds).padStart(2, "0")}.${tenths % 10}`;
}

/** "0:23": a project's length on its card, in minutes and whole seconds. */
export function formatLength(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/** Seconds with at most one decimal, for a number field. */
export function toSeconds(ms: number): number {
  return Math.round(ms / 100) / 10;
}

/** A number field's seconds as whole milliseconds; anything not a number is `undefined`. */
export function fromSeconds(value: number | string): number | undefined {
  const seconds = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(seconds) ? Math.round(seconds * 1000) : undefined;
}

/**
 * Live's timecode: "00:04.20" (minutes, seconds and hundredths) for where the playhead is, or
 * "00:04" (minutes and seconds) for a length and the ruler.
 */
export function formatTimecode(ms: number, hundredths = false): string {
  const whole = Math.max(0, ms);
  const seconds = Math.floor(whole / 1000);
  const pad = (value: number) => String(value).padStart(2, "0");
  const base = `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;
  return hundredths ? `${base}.${pad(Math.floor((whole % 1000) / 10))}` : base;
}

/**
 * The timecode's total, "00:14" for a video of 13.12 s: whole seconds, rounded up, so the
 * playhead's "00:13.12" never reads past the length it plays to (docs/specs/editor/phone-polish.md).
 */
export function formatTotalTimecode(ms: number): string {
  return formatTimecode(Math.ceil(Math.max(0, ms) / 1000) * 1000);
}
